import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fortnightStart, addDays, todayMel } from "@/lib/time";
import { ChevronLeft, ChevronRight, CalendarDays, Clock } from "lucide-react";

export default function StaffRoster() {
  const [fs, setFs] = useState(fortnightStart(todayMel()));
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/roster/me", { params: { fortnight_start: fs } });
    setData(data);
  }, [fs]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  // group shifts by date
  const byDate = {};
  data.shifts.forEach((s) => { (byDate[s.date] = byDate[s.date] || []).push(s); });
  const days = Array.from({ length: 14 }, (_, i) => addDays(fs, i));

  return (
    <div className="space-y-6" data-testid="staff-roster">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">My Roster</h1>
          <p className="text-muted-foreground">Rostered {data.total_rostered_display} this fortnight</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setFs(addDays(fs, -14))} data-testid="roster-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium px-2">{data.fortnight_start_display} – {data.fortnight_end_display}</span>
          <Button variant="outline" size="icon" onClick={() => setFs(addDays(fs, 14))} data-testid="roster-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {data.shifts.length === 0 && (
        <Card className="p-8 rounded-xl border-border text-center">
          <CalendarDays className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">No published roster for this fortnight yet.</p>
        </Card>
      )}

      <div className="grid gap-3" data-testid="roster-days">
        {days.map((d) => {
          const shifts = byDate[d];
          if (!shifts) return null;
          const dt = new Date(d + "T00:00:00");
          const [y, m, day] = d.split("-");
          return (
            <Card key={d} className="p-4 rounded-xl border-border">
              <div className="flex items-center justify-between mb-2">
                <p className="font-heading font-semibold">
                  {dt.toLocaleDateString("en-AU", { weekday: "long" })} <span className="text-muted-foreground font-normal">{day}/{m}/{y}</span>
                </p>
              </div>
              <div className="space-y-2">
                {shifts.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 text-sm bg-secondary rounded-lg px-3 py-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="font-medium">{s.start_display} – {s.end_display}</span>
                    <span className="text-muted-foreground ml-auto">{s.rostered_display}</span>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
